import { Button, Flex, Stack, Switch, Text } from '@mantine/core';
import type { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AgendaFileCard } from './AgendaFileCard';

type Props = {
  agendaFiles: UploadedFile[];
  selectedAgendaFileId: string | undefined;
  isUploading: boolean;
  isGenerating: boolean;
  onSelectFile: (fileId: string) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onGenerate: () => void;
  onBack: () => void;
  summarise?: boolean;
  onSummariseChange?: (value: boolean) => void;
  showSummariseToggle?: boolean;
};

export const AgendaSelection = ({
  agendaFiles,
  selectedAgendaFileId,
  isUploading,
  isGenerating,
  onSelectFile,
  onFileUpload,
  onGenerate,
  onBack,
  summarise,
  onSummariseChange,
  showSummariseToggle,
}: Props) => {
  return (
    <Stack gap="xs">
      <Flex justify="space-between" align="center">
        <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
          Select agenda to generate a meeting note
        </Text>
        <Text
          size="textSm"
          fw={FONT_WEIGHTS.semibold}
          c="brand.6"
          style={{ cursor: 'pointer' }}
          onClick={onBack}
        >
          Back
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.7">
          {agendaFiles.length} document{agendaFiles.length !== 1 ? 's' : ''} found
        </Text>
        <Button component="label" variant="subtle" loading={isUploading} size="xs">
          Upload agenda
          <input
            type="file"
            accept=".pdf,.doc,.docx,.txt,.md"
            style={{ display: 'none' }}
            onChange={onFileUpload}
          />
        </Button>
      </Flex>
      <Stack gap="xxs">
        {agendaFiles.length === 0 ? (
          <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.7">
            Upload a meeting agenda (PDF, Word, or text file) using the button above.
          </Text>
        ) : (
          agendaFiles.map(file => (
            <AgendaFileCard
              key={file.id}
              file={file}
              isSelected={selectedAgendaFileId === file.id}
              onSelect={() => onSelectFile(file.id)}
            />
          ))
        )}
      </Stack>
      {showSummariseToggle && (
        <Flex justify="space-between" align="center">
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
            Summarise meeting note
          </Text>
          <Switch
            checked={summarise ?? true}
            onChange={event => onSummariseChange?.(event.currentTarget.checked)}
          />
        </Flex>
      )}
      {selectedAgendaFileId && (
        <Button onClick={onGenerate} loading={isGenerating} mt="xs">
          Generate meeting note
        </Button>
      )}
    </Stack>
  );
};
